import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Sparkles, 
  Bug, 
  Zap, 
  Shield,
  Calendar,
  User,
  ChevronDown,
  X,
  Save,
  FileText,
  Tag
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const categoryConfig = {
  feature: { label: 'New Feature', icon: Sparkles, color: 'bg-green-100 text-green-700 border-green-200' },
  fix: { label: 'Bug Fix', icon: Bug, color: 'bg-red-100 text-red-700 border-red-200' },
  improvement: { label: 'Improvement', icon: Zap, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  security: { label: 'Security', icon: Shield, color: 'bg-purple-100 text-purple-700 border-purple-200' }
};

const AdminChangelog = () => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    description: '',
    category: 'feature',
    changes: ['']
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('rooftrack_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    loadChangelog();
    loadStats();
  }, [filterCategory]);

  const loadChangelog = async () => {
    try {
      setIsLoading(true);
      const params = filterCategory ? `?category=${filterCategory}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/changelog${params}`, {
        headers: getAuthHeaders()
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error loading changelog:', error);
      toast.error('Failed to load changelog');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/changelog/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        changes: formData.changes.filter(c => c.trim() !== '')
      };
      
      if (editingEntry) {
        await axios.put(`${BACKEND_URL}/api/changelog/${editingEntry.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Changelog entry updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/changelog`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Changelog entry created');
      }
      
      setShowModal(false);
      resetForm();
      loadChangelog();
      loadStats();
    } catch (error) {
      console.error('Error saving changelog:', error);
      toast.error(error.response?.data?.detail || 'Failed to save changelog entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this changelog entry?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/changelog/${entryId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Changelog entry deleted');
      loadChangelog();
      loadStats();
    } catch (error) {
      console.error('Error deleting changelog:', error);
      toast.error('Failed to delete changelog entry');
    }
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      version: entry.version,
      title: entry.title,
      description: entry.description,
      category: entry.category,
      changes: entry.changes?.length > 0 ? entry.changes : ['']
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      version: '',
      title: '',
      description: '',
      category: 'feature',
      changes: ['']
    });
  };

  const addChangeItem = () => {
    setFormData(prev => ({
      ...prev,
      changes: [...prev.changes, '']
    }));
  };

  const updateChangeItem = (index, value) => {
    setFormData(prev => ({
      ...prev,
      changes: prev.changes.map((c, i) => i === index ? value : c)
    }));
  };

  const removeChangeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      changes: prev.changes.filter((_, i) => i !== index)
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Changelog Management</h1>
          <p className="text-gray-600 mt-1">Track and document all updates, fixes, and new features</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Entry
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_entries}</p>
                <p className="text-sm text-gray-500">Total Entries</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_category.features}</p>
                <p className="text-sm text-gray-500">Features</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Bug className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_category.fixes}</p>
                <p className="text-sm text-gray-500">Bug Fixes</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_category.improvements}</p>
                <p className="text-sm text-gray-500">Improvements</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_category.security}</p>
                <p className="text-sm text-gray-500">Security</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Label className="text-gray-700">Filter by category:</Label>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Categories</option>
          <option value="feature">Features</option>
          <option value="fix">Bug Fixes</option>
          <option value="improvement">Improvements</option>
          <option value="security">Security</option>
        </select>
      </div>

      {/* Changelog Entries */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Loading changelog...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No changelog entries yet</p>
            <p className="text-gray-400 text-sm">Click "Add Entry" to create your first changelog</p>
          </div>
        ) : (
          entries.map((entry) => {
            const config = categoryConfig[entry.category] || categoryConfig.feature;
            const Icon = config.icon;
            
            return (
              <div
                key={entry.id}
                className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color.split(' ')[0]}`}>
                      <Icon className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="bg-gray-100 text-gray-700 text-xs font-mono px-2 py-1 rounded">
                          v{entry.version}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded border ${config.color}`}>
                          {config.label}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{entry.title}</h3>
                      <p className="text-gray-600 mt-1">{entry.description}</p>
                      
                      {entry.changes && entry.changes.length > 0 && (
                        <ul className="mt-3 space-y-1">
                          {entry.changes.map((change, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="text-green-500 mt-0.5">•</span>
                              {change}
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(entry.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {entry.created_by_name}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(entry)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(entry.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingEntry ? 'Edit Changelog Entry' : 'Add Changelog Entry'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="version">Version *</Label>
                  <Input
                    id="version"
                    value={formData.version}
                    onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                    placeholder="1.2.3"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="feature">New Feature</option>
                    <option value="fix">Bug Fix</option>
                    <option value="improvement">Improvement</option>
                    <option value="security">Security</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Brief description of the change"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description *</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detailed explanation of what was changed and why"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[100px]"
                  required
                />
              </div>
              
              <div>
                <Label>Specific Changes (bullet points)</Label>
                <div className="space-y-2 mt-2">
                  {formData.changes.map((change, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-gray-400">•</span>
                      <Input
                        value={change}
                        onChange={(e) => updateChangeItem(index, e.target.value)}
                        placeholder="Describe a specific change"
                        className="flex-1"
                      />
                      {formData.changes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChangeItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addChangeItem}
                  className="mt-2"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Change
                </Button>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  {editingEntry ? 'Update Entry' : 'Create Entry'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminChangelog;
